import Title from "antd/es/typography/Title"
import PageHeader from "modules/layout/dashboard/main/page-header"
import { useTranslation } from "react-i18next"
import { useMySubstitutions } from "./queries"
import Loader from "components/loader"
import { Button, Empty, Space, Tag } from "antd"
import OverviewCardWithDate from "components/overview-card-with-date"
import { ReactComponent as InfoIcon } from "assets/icons/info.svg"
import { ReactComponent as AcceptSubstitutionIcon } from "assets/icons/accept-person.svg"
import { ReactComponent as DeleteIcon } from "assets/icons/delete.svg"
import { IMySubstitution } from "./types"
import { usePopup } from "utils/use-popup"
import { IEventDetail } from "pages/events/types"
import EventDetailsModal from "../event-details-modal"
import DeleteMySubstitution from "./delete"
import SubstitutionsRequestsDrawer from "./substitutions-requests-drawer"

const statusTagColors = (status: IMySubstitution["status"]) => {
  switch (status) {
    case "draft":
      return "var(--color-accent2)"
    case "replaced":
      return "var(--color-success)"
    default:
      return "var(--color-primary)"
  }
}

const MySubstitutions = () => {
  const { t } = useTranslation()
  const { data, isLoading } = useMySubstitutions()

  const eventDetailsModal = usePopup<IEventDetail>()
  const substitutionRequestsDrawer = usePopup<string>()
  const deleteSubstitutionModal = usePopup<string>()

  return (
    <>
      <PageHeader
        title={<Title level={4}>{t("assignations.my_substitutions")}</Title>}
        hasBackButton={false}
      />
      {isLoading ? (
        <Loader />
      ) : data?.length === 0 ? (
        <Empty
          style={{
            marginTop: "var(--size-10)",
          }}
          description={t("global.no_x_found", {
            entity: t("assignations.substitution_one"),
            context: "female",
            count: 1,
          })}
        />
      ) : (
        <Space direction="vertical">
          {data?.map((substitutionRequest) => {
            const {
              id,
              event,
              status,
              displayed_status,
              count_propositions,
              replacement,
            } = substitutionRequest

            return (
              <OverviewCardWithDate
                key={id}
                date={event.start_date}
                actions={[
                  <Tag
                    style={{
                      marginRight: 0,
                    }}
                    color={statusTagColors(status)}
                  >
                    {displayed_status}
                  </Tag>,
                  <Button
                    style={{
                      width: "100%",
                    }}
                    icon={<InfoIcon height={20} />}
                    size="small"
                    type="text"
                    onClick={() => eventDetailsModal.open(event)}
                  />,
                  <Button
                    style={{
                      width: "100%",
                    }}
                    icon={<AcceptSubstitutionIcon height={17} />}
                    size="small"
                    type="text"
                    disabled={
                      count_propositions <= 0 ||
                      status === "replaced" ||
                      ["execution", "terminated", "closed"].includes(
                        event.status!
                      )
                    }
                    onClick={() => substitutionRequestsDrawer.open(id)}
                  />,
                  <Button
                    style={{
                      width: "100%",
                    }}
                    icon={<DeleteIcon height={17} />}
                    size="small"
                    type="text"
                    disabled={
                      status === "replaced" ||
                      ["execution", "terminated", "closed"].includes(
                        event.status
                      )
                    }
                    onClick={() => deleteSubstitutionModal.open(id)}
                  />,
                ]}
                Content={
                  <>
                    <p>{event.name}</p>
                    <p>
                      {event.start_hour}
                      {" - "}
                      {event.end_hour}
                    </p>
                    <p>{event.place?.name ?? event.activity_room?.name}</p>
                    <p>
                      {count_propositions}{" "}
                      {t("global.application", { count: count_propositions })}
                    </p>
                    {replacement && (
                      <p>
                        {t("labels.substitute")} : {replacement.name}
                      </p>
                    )}
                  </>
                }
              />
            )
          })}
        </Space>
      )}
      <EventDetailsModal
        {...eventDetailsModal}
        open={eventDetailsModal.isOpen}
      />
      <SubstitutionsRequestsDrawer
        {...substitutionRequestsDrawer}
        open={substitutionRequestsDrawer.isOpen}
      />
      <DeleteMySubstitution
        {...deleteSubstitutionModal}
        open={deleteSubstitutionModal.isOpen}
      />
    </>
  )
}

export default MySubstitutions
